import { useCallback, useState } from 'react';
import { putMedia, deleteMedia, applyMainPhotoTag, KIND } from '../data/media';
import { pickPhotos, pickVideo } from './capture';
import { MAIN_PHOTO_TAG } from '../data/constants';
import { useToast } from '../ui';

/**
 * Capture → store → reference, in one hook.
 *
 * Screens hold media REFERENCES in their draft record and never the bytes; the
 * Blob goes straight into IndexedDB and the reference carries its `mediaId`.
 * That is what keeps a supplier document under Mongo's 16MB ceiling and makes a
 * dropped upload cost one file instead of the batch.
 *
 * Rule Q1's main-photo bookkeeping lives here: the FIRST photo taken becomes
 * the Main Product Photo, and on an upload the first becomes main only if none
 * exists yet.
 */
export default function useMedia({ supplierId = '', productId = '' } = {}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  /**
   * Add photos. `fromCamera` distinguishes the two rule-Q1 cases: a photo
   * TAKEN becomes main; a photo UPLOADED becomes main only if there isn't one.
   */
  const addPhotos = useCallback(
    async ({ existing = [], tag = 'Product', fromCamera = false, forProductId } = {}) => {
      setBusy(true);
      try {
        const picked = await pickPhotos({ capture: fromCamera });
        if (!picked.length) return existing;

        const pid = forProductId !== undefined ? forProductId : productId;
        const hasMain = existing.some((p) => p.tag === MAIN_PHOTO_TAG);
        const refs = [];

        for (let i = 0; i < picked.length; i += 1) {
          const isFirst = i === 0;
          const shouldBeMain = tag === 'Product' && isFirst && (fromCamera || !hasMain);
          const ref = await putMedia({
            blob: picked[i].blob,
            kind: KIND.PHOTO,
            tag: shouldBeMain ? MAIN_PHOTO_TAG : tag,
            supplierId,
            productId: pid || '',
            width: picked[i].width,
            height: picked[i].height,
          });
          refs.push(ref);
        }

        let next = [...existing, ...refs];
        // A newly taken main photo demotes the previous one.
        const newMain = refs.find((r) => r.tag === MAIN_PHOTO_TAG);
        if (newMain && hasMain) next = applyMainPhotoTag(next, newMain.id, pid || '');
        return next;
      } catch (e) {
        toast('Could not add that photo');
        return existing;
      } finally {
        setBusy(false);
      }
    },
    [supplierId, productId, toast],
  );

  /** Rule V1 is enforced in pickVideo, before anything is stored. */
  const addVideo = useCallback(
    async ({ existing = [], forProductId } = {}) => {
      setBusy(true);
      try {
        const picked = await pickVideo({ capture: true });
        if (!picked) return existing;
        if (picked.rejected) {
          toast(picked.rejected);
          return existing;
        }
        const ref = await putMedia({
          blob: picked.blob,
          kind: KIND.VIDEO,
          tag: 'Video',
          supplierId,
          productId: forProductId !== undefined ? forProductId : productId,
          duration: picked.duration,
        });
        return [...existing, ref];
      } catch (e) {
        toast('Could not add that video');
        return existing;
      } finally {
        setBusy(false);
      }
    },
    [supplierId, productId, toast],
  );

  /** Called by useRecorder's onDone — the recorder owns the microphone. */
  const addVoice = useCallback(
    async ({ existing = [], blob, duration, forProductId } = {}) => {
      try {
        const ref = await putMedia({
          blob,
          kind: KIND.VOICE,
          tag: 'Voice note',
          supplierId,
          productId: forProductId !== undefined ? forProductId : productId,
          duration,
        });
        return [...existing, ref];
      } catch (e) {
        toast('Could not save that voice note');
        return existing;
      }
    },
    [supplierId, productId, toast],
  );

  /** Re-tag, applying rule Q1's uniqueness when the new tag is the main one. */
  const retag = useCallback((photos, photo, tag, newProductId) => {
    const pid = newProductId !== undefined ? newProductId : photo.productId;
    if (tag === MAIN_PHOTO_TAG) return applyMainPhotoTag(photos, photo.id, pid);
    return photos.map((p) => (p.id === photo.id ? { ...p, tag, productId: pid } : p));
  }, []);

  /** Remove the reference AND the stored Blob — nothing orphaned. */
  const remove = useCallback(async (list, item) => {
    await deleteMedia(item.mediaId || item.id);
    return list.filter((x) => x.id !== item.id);
  }, []);

  return { addPhotos, addVideo, addVoice, retag, remove, busy };
}
